import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  useTheme,
  Slide,
  styled,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import LogoutIcon from "@mui/icons-material/Logout";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onClose: () => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 12,
    padding: theme.spacing(4),
    width: "100%",
    maxWidth: 420,
    boxShadow: theme.shadows[4],
  },
}));

const IconCircle = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: "50%",
  width: 60,
  height: 60,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
  marginBottom: theme.spacing(2),
}));

const LogoutButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  fontWeight: 600,
  borderRadius: 8,
  padding: "0.7rem 2rem",
  textTransform: "none",
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: 600,
  borderRadius: 8,
  padding: "0.7rem 2rem",
  textTransform: "none",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const LogoutDialog = ({ open, onClose }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const handleLogout = () => {
    localStorage.removeItem("currentUserToken");
    localStorage.removeItem("currentUserName");
    window.location.href = "/auth";
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      aria-labelledby="logout-dialog-title"
    >
      <IconCircle>
        <LogoutIcon sx={{ color: theme.palette.error.main, fontSize: 32 }} />
      </IconCircle>

      <DialogTitle
        id="logout-dialog-title"
        sx={{
          textAlign: "center",
          fontSize: "1.5rem",
          fontWeight: 700,
          paddingBottom: 0,
        }}
      >
        {t("Logout")}
      </DialogTitle>

      <DialogContent>
        <Typography
          variant="body1"
          align="center"
          sx={{ color: theme.palette.text.secondary }}
        >
          {t("Do you really want to log out? Your session will be ended.")}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", gap: 2, marginTop: 1 }}>
        <CancelButton onClick={onClose}>{t("Cancel")}</CancelButton>
        <LogoutButton onClick={handleLogout}>{t("Yes") + ", " + t("Logout")}</LogoutButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default LogoutDialog;
